import {
    Asset,
    Building,
    Customer,
    CustomerTheme,
    Entity,
    Floor,
    Gateway,
    Invoice,
    LegalPerson,
    Product,
    Project,
    ProjectEntitlement,
    Space,
} from "@the-real-insight/tri-model";
import { configuration } from "./Configuration";
import { ApplicationError, TenderPlanLimitError, UnauthorizedError } from "./general/Errors";
import { useAtom } from "jotai";
import { authenticationAtom } from "./State";
import { authenticatedFetch } from "./JWTAuthService";

const handleResponseArray = async (
  response: any,
  callback: (entity: any) => void,
): Promise<any[]> => {
  if (response.ok) {
    const result = await response.json();

    if (result && Array.isArray(result)) {
      return result.map((entity: any) => callback(entity));
    } else {
      return [];
    }
  } else {
    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    // Try to get error message from response body
    let errorMessage = `${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // If JSON parsing fails, response body might be text or empty
      // Use the default error message
    }

    console.error("handleResponseArray error:", response.status, errorMessage);

    if (response.status === 400) {
      throw new ApplicationError(errorMessage);
    } else {
      throw new ApplicationError(`Request failed: ${errorMessage}`);
    }
  }
};

export const useAdministrationAPI = () => {
  const baseUrl = configuration.serverUrl;
  const [authentication] = useAtom(authenticationAtom) as any;

  const processResponse = async (response: Response) => {
    if (response.ok) {
      const result = await response.json();

      if (result.error) {
        console.error(
          "❌ processResponse found error in result:",
          result.error,
        );
        throw new ApplicationError(result.error);
      }

      return result;
    } else {
      console.error("❌ processResponse response not ok:", {
        status: response.status,
        statusText: response.statusText,
      });

      if (response.status === 401) {
        throw new UnauthorizedError();
      } else if (response.status === 422) {
        // A value the server turned away, e.g. a customer attribute of the wrong type — the
        // message names the attribute ("Wartung › Intervall: „oft“ ist keine Zahl.").
        const body = await response.json().catch(() => null);
        throw new ApplicationError(body?.message || body?.error || response.statusText);
      } else {
        throw Error();
      }
    }
  };

  const getCustomers = async () => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.json();
  };

  /**
   * The roles defined for a customer, as `GET /:customer/roles` reports them.
   *
   * Used by the volume settings to offer real roles instead of a free-text field: a mistyped role
   * id is not rejected anywhere (the server's validator is pure and cannot check the role exists),
   * and its only symptom would be a volume nobody can see.
   */
  const getCustomerRoles = async (customerId: string) => {
    const response = await authenticatedFetch(`${baseUrl}/${customerId}/roles`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const createCustomer = async (customer: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers`,
      {
        method: "POST",
        body: JSON.stringify(customer),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.json();
  };

  const updateCustomer = async (customer: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customer.id}`,
      {
        method: "PUT",
        body: JSON.stringify(customer),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.json();
  };

  const updateCustomerLogo = async (customerId: string, form: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/logo`,
      {
        method: "PUT",
        body: form,
        headers: {
          //'Content-Type': 'application/json',
        },
      },
    );

    return response.json();
  };

  const deleteCustomer = async (customerId: string) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.json();
  };

  const getUsers = async (customerId: string) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/users`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.json();
  };

  const createUser = async (customerId: string, user: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/users`,
      {
        method: "POST",
        body: JSON.stringify(user),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    // The tender plan allows no more users. Without this the 409 body would be returned like a
    // created user, and the panel would report an invitation that never happened.
    const planLimit = await TenderPlanLimitError.fromResponse(response);
    if (planLimit) {
      throw planLimit;
    }

    return response.json();
  };

  const updateUser = async (customerId: string, user: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/users/${user.id}`,
      {
        method: "PUT",
        body: JSON.stringify(user),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.json();
  };

  /** Einladung erneut senden (neues temporäres Passwort). 409: Nutzer hat schon ein eigenes Passwort. */
  const resendInvitation = async (customerId: string, user: any): Promise<{ invitationSent: boolean; ssoOnly?: boolean; refused?: "password-set" | "too-soon" }> => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${encodeURIComponent(customerId)}/users/${encodeURIComponent(user._id ?? user.id)}/invitation`,
      { method: "POST", headers: { "Content-Type": "application/json" } },
    );
    // Eine Fehlerseite des Proxys (HTML) ist kein JSON.
    const data = await response.json().catch(() => ({}));
    if (!response.ok && response.status !== 409 && response.status !== 429) throw new ApplicationError(data.error || response.statusText);
    return data;
  };

  const deleteUser = async (customerId: any, user: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/users/${user._id}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new ApplicationError(data.error || response.statusText);
    }

    return data;
  };

  const ingestData = async (
    customerId: string,
    entityType: string,
    form: any,
  ) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/ingest/data/${entityType}/files`,
      {
        method: "POST",
        body: form,
        headers: {
          //'Content-Type': 'application/json',
        },
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      if (response.status === 400) {
        throw new ApplicationError((await response.json()).message);
      } else if (response?.status === 401) {
        console.log("Ingest data unauthorized");
        //throw new UnauthorizedError();
      }
    }
  };

  const getInvoiceForecast = async (
    customerId: string,
    from: Date,
    to: Date,
  ) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/invoices/forecast?from=${from.toISOString()}&to=${to.toISOString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return handleResponseArray(response, (entry: any) => {
      return new Invoice(entry);
    });
  };

  const getInvoices = async (customerId: string, from: Date, to: Date) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/invoices?from=${from.toISOString()}&to=${to.toISOString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      const result = await response.json();

      console.log("Invoices >>>", result);

      // Handle new response structure: { inbound: Invoice[], outbound: Invoice[] }
      if (
        result &&
        typeof result === "object" &&
        ("inbound" in result || "outbound" in result)
      ) {
        return {
          inbound: (result.inbound || []).map((properties: any) => {
            return new Invoice(properties);
          }),
          outbound: (result.outbound || []).map((properties: any) => {
            return new Invoice(properties);
          }),
        };
      }

      // Fallback for backward compatibility (if response is still an array)
      return {
        inbound: (result || []).map((properties: any) => {
          return new Invoice(properties);
        }),
        outbound: [],
      };
    } else {
      if (response.status === 400) {
        throw new ApplicationError((await response.json()).message);
      } else if (response?.status === 401) {
        throw new UnauthorizedError();
      }
      // Default return for other error cases
      return { inbound: [], outbound: [] };
    }
  };

  const getInvoiceById = async (customerId: string, invoiceId: string) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/invoices?invoiceId=${invoiceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      const result = await response.json();
      return new Invoice(result);
    } else {
      if (response.status === 400) {
        throw new ApplicationError((await response.json()).message);
      } else if (response?.status === 401) {
        throw new UnauthorizedError();
      }
      throw new ApplicationError("Failed to fetch invoice");
    }
  };

  const getAllInvoices = async (
    from: Date,
    to: Date,
    openOnly: boolean,
    filter: string,
  ) => {
    let url = `${baseUrl}/administration/invoices?from=${from.toISOString()}&to=${to.toISOString()}&openOnly=${openOnly}`;

    if (filter) {
      url += `&filter=${filter}`;
    }

    const response = await authenticatedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return (await response.json()).map((properties: any) => {
        return new Invoice(properties);
      });
    } else {
      if (response.status === 400) {
        throw new ApplicationError((await response.json()).message);
      } else if (response?.status === 401) {
        throw new UnauthorizedError();
      }
    }
  };

  const deleteInvoice = async (invoiceId: string) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/invoices/${invoiceId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      if (response.status === 400) {
        throw new ApplicationError((await response.json()).message);
      } else if (response?.status === 401) {
        throw new UnauthorizedError();
      }
      throw new ApplicationError("Failed to delete invoice");
    }
  };

  const runBillingGlobal = async (period: Date) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/billing?period=${period.toISOString()}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      if (response.status === 400) {
        throw new ApplicationError((await response.json()).message);
      } else if (response?.status === 401) {
        throw new UnauthorizedError();
      }
    }
  };

  const runBilling = async (customers: Customer[], period: Date) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/billing?period=${period.toISOString()}`,
      {
        method: "POST",
        body: JSON.stringify(customers),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      if (response.status === 400) {
        throw new ApplicationError((await response.json()).message);
      } else if (response?.status === 401) {
        throw new UnauthorizedError();
      }
    }
  };

  const updateEntity = async (entity: Entity) => {
    const response = await authenticatedFetch(
      `${baseUrl}/entities/${entity.getTypeName()}`,
      {
        method: "POST",
        body: JSON.stringify(entity),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const updateInvoices = async (invoices: Invoice[]) => {
    const response = await authenticatedFetch(`${baseUrl}/invoices`, {
      method: "POST",
      body: JSON.stringify(invoices),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const createOrUpdateProduct = async (product: Product) => {
    const response = await authenticatedFetch(`${baseUrl}/products`, {
      method: "PUT",
      body: JSON.stringify(product),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  // Upload one or more files to a product's documents. Each entry's `role` is
  // used as the multipart field name; the server reads it to compute the storage
  // path /Product/<Mfg> - <City>/<Model>/<role>/<filename>, registers a
  // ManagedFile, kicks off RAG processing, and appends the new _ids to
  // Product.documents. Returns { added, documents } per the new endpoint.
  const uploadProductDocuments = async (
    productId: string,
    items: { file: File; role: string }[],
  ) => {
    if (!productId) throw new Error("productId required");
    if (!items || items.length === 0) return { added: [], documents: [] };
    const form = new FormData();
    for (const { file, role } of items) {
      const safeRole = (role || "other").toLowerCase();
      form.append(safeRole, file, file.name);
    }
    const response = await authenticatedFetch(
      `${baseUrl}/products/${productId}/documents`,
      { method: "POST", body: form },
    );
    return processResponse(response);
  };

  // Upload one or more files to any entity's `documents` array (Building, Asset,
  // Floor, Space, …). Each entry's `role` becomes the multipart field name; the
  // server stores the file under /<EntityType>/<id>/<role>/<filename>, registers
  // a private ManagedFile and appends its _id to the entity's `documents`.
  // Returns { added, documents } from POST /entities/:type/:id/documents.
  const uploadEntityDocuments = async (
    entityType: string,
    id: string,
    items: { file: File; role: string }[],
  ) => {
    if (!entityType || !id) throw new Error("entityType and id required");
    if (!items || items.length === 0) return { added: [], documents: [] };
    const form = new FormData();
    for (const { file, role } of items) {
      const safeRole = (role || "other").toLowerCase();
      form.append(safeRole, file, file.name);
    }
    const response = await authenticatedFetch(
      `${baseUrl}/entities/${entityType}/${id}/documents`,
      { method: "POST", body: form },
    );
    return processResponse(response);
  };

  const createOrUpdateCustomerTheme = async (customerTheme: CustomerTheme) => {
    customerTheme.customer = authentication.customer;

    const endpoint = `${baseUrl}/theme`;

    console.log("🔄 SAVE THEME API CALL:", {
      customer: authentication.customer,
      hasLogoURL: !!customerTheme.logoURL,
      logoURL: customerTheme.logoURL,
      endpoint: endpoint,
      themeObject: customerTheme,
    });

    const response = await authenticatedFetch(endpoint, {
      method: "POST",
      body: JSON.stringify(customerTheme),
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log("📡 SAVE THEME RESPONSE:", {
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
    });

    const result = await processResponse(response);
    console.log("✅ SAVE THEME RESULT:", result);
    return result;
  };

  const getLatestOperatingState = async () => {
    const response = await authenticatedFetch(
      `${baseUrl}/customers/${authentication.customer}/operating-states/latest`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const createFloor = async (building: Building, floor: Floor) => {
    const response = await authenticatedFetch(
      `${baseUrl}/buildings/${building._id}/floors`,
      {
        method: "POST",
        body: JSON.stringify(floor),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const updateFloor = async (floor: Floor) => {
    const response = await authenticatedFetch(`${baseUrl}/floors`, {
      method: "PUT",
      body: JSON.stringify(floor),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const deleteFloor = async (floor: Floor) => {
    const response = await authenticatedFetch(`${baseUrl}/floors`, {
      method: "DELETE",
      body: JSON.stringify(floor),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const updateFloorplanImage = async (floor: Floor, form: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/floors/${floor._id}/floorplanImage`,
      {
        method: "PUT",
        body: form,
        headers: {
          //'Content-Type': 'application/json',
        },
      },
    );

    return response.json();
  };

  /**
   * Read the server's chunked NDJSON DWG-map stream: forwards each recognition `progress`/`ping`
   * event to `onProgress`, throws on an `error` event, and resolves with the final `done` summary
   * ({ type:'done', floors:[{ floorId, label, level, spaces, assets, floorplan, created }] }).
   */
  const readDwgMapStream = async (
    response: any,
    onProgress?: (event: any) => void,
  ): Promise<any> => {
    if (!response.ok) {
      throw new Error(`DWG mapping failed: HTTP ${response.status}`);
    }
    if (!response?.body || typeof response.body.getReader !== "function") {
      // No stream (e.g. an error status without a body) — surface it.
      throw new Error(`DWG mapping failed: HTTP ${response?.status ?? "?"}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let done: any = null;

    const handleLine = (rawLine: string) => {
      const line = rawLine.trim();
      if (!line) {
        return;
      }
      let event: any;
      try {
        event = JSON.parse(line);
      } catch {
        return;
      }
      if (event.type === "error") {
        throw new Error(event.message || "DWG mapping error");
      }
      if (event.type === "done") {
        done = event;
      }
      onProgress?.(event);
    };

    for (;;) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        handleLine(line);
      }
    }
    if (buffer.trim()) {
      handleLine(buffer);
    }
    if (!done) {
      throw new Error("DWG mapping failed: stream ended without a completion event");
    }
    return done;
  };

  /** Upload a single .dwg for an existing floor → recognition server → floorplan + spaces + assets. */
  const mapFloorDwg = async (
    floor: Floor,
    file: File,
    onProgress?: (event: any) => void,
    overwrite?: {
      floors?: boolean;
      spaces?: boolean;
      doors?: boolean;
      assets?: boolean;
      floorplanImage?: boolean;
    },
  ) => {
    const form = new FormData();
    form.append("file", file);
    // Absent means merge, same as the raster route — the server defaults that
    // way too, so an old client cannot overwrite by omission.
    form.append("overwriteFloors", String(!!overwrite?.floors));
    form.append("overwriteSpaces", String(!!overwrite?.spaces));
    form.append("overwriteDoors", String(!!overwrite?.doors));
    form.append("overwriteAssets", String(!!overwrite?.assets));
    form.append("overwriteFloorplanImage", String(!!overwrite?.floorplanImage));
    const response = await authenticatedFetch(
      `${baseUrl}/floors/${floor._id}/map-dwg`,
      { method: "POST", body: form, headers: {} },
    );

    return readDwgMapStream(response, onProgress);
  };

  /**
   * Classify a plan file without analyzing it:
   * "dwg" | "pdf-vector" | "escape-plan" | "scanned-architectural" | "unsupported".
   *
   * A raster scan PDF and a vector CAD PDF are both `application/pdf` and can only be told apart
   * server-side, so the uploader asks here before choosing between the raster analyze path
   * (analyze-floorplan) and the DWG/vector map path (map-dwg).
   */
  const detectPlanType = async (file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    const response = await authenticatedFetch(`${baseUrl}/plan-type`, {
      method: "POST",
      body: form,
      headers: {},
    });
    const result = await processResponse(response);

    return (result && (result as any).type) || "unsupported";
  };

  /**
   * Upload a raster floorplan (a scanned Flucht- und Rettungsplan) for one floor.
   *
   * The server cleans the image, detects rooms and doors, stores the cleaned PNG
   * as the floor's floorplan image and creates one `Space` per detected room —
   * the same route tri-app uses. Room polygons come back in world coordinates,
   * derived from the cleaned image's pixel scale, so they line up with what the
   * floor plan editor draws.
   *
   * Unlike the DWG import this is a single request with a JSON answer, not a
   * progress stream: the analysis happens in one step on the recognition
   * service, so there is nothing to report in between.
   */
  const analyzeFloorplanImage = async (
    floor: Floor,
    file: File,
    overwrite?: {
      floors?: boolean;
      spaces?: boolean;
      doors?: boolean;
      assets?: boolean;
      floorplanImage?: boolean;
    },
  ) => {
    const form = new FormData();
    form.append("floorplanImage", file);
    // Absent means merge. The server defaults the same way, so an old client
    // cannot accidentally overwrite by omission.
    form.append("overwriteFloors", String(!!overwrite?.floors));
    form.append("overwriteSpaces", String(!!overwrite?.spaces));
    form.append("overwriteDoors", String(!!overwrite?.doors));
    form.append("overwriteAssets", String(!!overwrite?.assets));
    form.append("overwriteFloorplanImage", String(!!overwrite?.floorplanImage));

    const response = await authenticatedFetch(
      `${baseUrl}/floors/${floor._id}/analyze-floorplan`,
      { method: "POST", body: form, headers: {} },
    );

    return processResponse(response);
  };

  /** Upload a .zip of .dwg files for a building → one floor per DWG (matched by level, else created). */
  const mapBuildingDwgZip = async (
    building: Building,
    file: File,
    onProgress?: (event: any) => void,
  ) => {
    const form = new FormData();
    form.append("file", file);
    const response = await authenticatedFetch(
      `${baseUrl}/buildings/${building._id}/map-dwg`,
      { method: "POST", body: form, headers: {} },
    );

    return readDwgMapStream(response, onProgress);
  };

  const createSpace = async (floor: Floor, space: Space) => {
    if (!floor) {
      throw Error("Cannot create space. Floor or building undefined.");
    }

    const response = await authenticatedFetch(
      `${baseUrl}/floors/${floor._id}/spaces`,
      {
        method: "POST",
        body: JSON.stringify(space),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const updateSpace = async (space: Space) => {
    const response = await authenticatedFetch(`${baseUrl}/spaces`, {
      method: "PUT",
      body: JSON.stringify(space),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const deleteSpace = async (space: Space) => {
    const response = await authenticatedFetch(`${baseUrl}/spaces`, {
      method: "DELETE",
      body: JSON.stringify(space),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const updateAsset = async (asset: Asset) => {
    const response = await authenticatedFetch(`${baseUrl}/assets`, {
      method: "PUT",
      body: JSON.stringify(asset),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const getAssetParts = async (assetId: string) => {
    const response = await authenticatedFetch(
      `${baseUrl}/${authentication.customer}/assets/${assetId}/parts`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const createAsset = async (asset: Asset) => {
    const response = await authenticatedFetch(`${baseUrl}/assets`, {
      method: "POST",
      body: JSON.stringify(asset),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const deleteAsset = async (asset: Asset) => {
    const response = await authenticatedFetch(
      `${baseUrl}/${authentication.customer}/assets/${asset._id}`,
      {
        method: "DELETE",
        body: JSON.stringify(asset),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const updateAssetPhotos = async (asset: Asset, form: any) => {
    const response = await authenticatedFetch(
      `${baseUrl}/assets/${asset._id}/photo`,
      {
        method: "PUT",
        body: form,
        headers: {
          //'Content-Type': 'application/json',
        },
      },
    );

    return response.json();
  };

  // @deprecated

  const getProjects = async () => {
    const response = await authenticatedFetch(`${baseUrl}/projects`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const result = await processResponse(response);

    return result.map((entry: any) => new Project(entry));
  };

  const createProject = async (project: Project) => {
    const response = await authenticatedFetch(`${baseUrl}/projects`, {
      method: "POST",
      body: JSON.stringify(project),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const updateProject = async (project: Project) => {
    const response = await authenticatedFetch(`${baseUrl}/projects`, {
      method: "PUT",
      body: JSON.stringify(project),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const deleteProject = async (project: Project) => {
    const response = await authenticatedFetch(`${baseUrl}/projects`, {
      method: "DELETE",
      body: JSON.stringify(project),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const getProjectEntitlements = async (project: Project) => {
    const response = await authenticatedFetch(
      `${baseUrl}/projects/${project._id}/projectEntitlements`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const createProjectEntitlement = async (
    projectEntitlement: ProjectEntitlement,
  ) => {
    const response = await authenticatedFetch(
      `${baseUrl}/projectEntitlements`,
      {
        method: "POST",
        body: JSON.stringify(projectEntitlement),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const updateProjectEntitlement = async (
    projectEntitlement: ProjectEntitlement,
  ) => {
    const response = await authenticatedFetch(
      `${baseUrl}/projectEntitlements`,
      {
        method: "PUT",
        body: JSON.stringify(projectEntitlement),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const deleteProjectEntitlement = async (
    projectEntitlement: ProjectEntitlement,
  ) => {
    const response = await authenticatedFetch(
      `${baseUrl}/projectEntitlements`,
      {
        method: "DELETE",
        body: JSON.stringify(projectEntitlement),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const createLegalPerson = async (legalPerson: LegalPerson) => {
    const response = await authenticatedFetch(`${baseUrl}/legalPersons`, {
      method: "POST",
      body: JSON.stringify(legalPerson),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const updateLegalPerson = async (legalPerson: LegalPerson) => {
    const response = await authenticatedFetch(`${baseUrl}/legalPersons`, {
      method: "PUT",
      body: JSON.stringify(legalPerson),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const deleteLegalPerson = async (legalPerson: LegalPerson) => {
    const response = await authenticatedFetch(`${baseUrl}/legalPersons`, {
      method: "DELETE",
      body: JSON.stringify(legalPerson),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const createGateway = async (customer: any) => {
    const response = await authenticatedFetch(`${baseUrl}/gateways`, {
      method: "POST",
      body: JSON.stringify(customer),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const updateGateway = async (gateway: any) => {
    const response = await authenticatedFetch(`${baseUrl}/gateways`, {
      method: "PUT",
      body: JSON.stringify(gateway),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  const deleteGateway = async (gateway: any) => {
    const response = await authenticatedFetch(`${baseUrl}/gateways`, {
      method: "DELETE",
      body: JSON.stringify(gateway),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return processResponse(response);
  };

  /**
   * Devices on a gateway, one page at a time.
   *
   * Paging and filtering happen at the gateway, not here. A gateway carrying the
   * demo fleet holds a hundred thousand devices; the whole list is about 47 MB
   * and sixteen seconds, which is more than the panel meant to browse it can
   * hold, let alone re-render on every keystroke.
   *
   * Returns `{ items, total }` either way: a gateway too old to page answers
   * with a plain array, and that is normalised here so the caller does not have
   * to know which one it is talking to.
   */
  const getDevicesFromGateway = async (
    gateway: Gateway,
    options: { page?: number; pageSize?: number; filter?: string } = {},
  ): Promise<{ items: any[]; total: number }> => {

    // Check if gateway.url is a full URL and extract the identifier
    let gatewayIdentifier = gateway.url;
    if (
      gateway.url &&
      (gateway.url.startsWith("http://") || gateway.url.startsWith("https://"))
    ) {
      // If it's a full URL, we might need to extract the identifier differently
      // For now, let's URL encode it to handle special characters
      gatewayIdentifier = encodeURIComponent(String(gateway.url));
      console.log(
        "🔧 Detected full URL, using encoded identifier:",
        gatewayIdentifier,
      );
    }

    const query = new URLSearchParams();

    if (options.page !== undefined) query.set("page", String(options.page));
    if (options.pageSize !== undefined) query.set("pageSize", String(options.pageSize));
    if (options.filter) query.set("filter", options.filter);

    const endpoint =
      `${baseUrl}/gateways/${gatewayIdentifier}/devices` +
      (query.size ? `?${query}` : "");

    try {
      const response = await authenticatedFetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ getDevicesFromGateway error response:", errorText);
        throw new Error(
          `Gateway devices request failed: ${response.status} ${response.statusText} - ${errorText}`,
        );
      }

      const result = await processResponse(response);

      return Array.isArray(result)
        ? { items: result, total: result.length }
        : { items: result?.items ?? [], total: result?.total ?? 0 };
    } catch (error) {
      console.error("💥 getDevicesFromGateway error:", error);
      throw error;
    }
  };

  const getGatewayStatus = async (gateway: Gateway) => {
    console.log("🔍 getGatewayStatus called with gateway:", {
      gateway,
      url: gateway.url,
      fullEndpoint: `${baseUrl}/gateways/${gateway.url}/status`,
    });

    // Check if gateway.url is a full URL and extract the identifier
    let gatewayIdentifier = gateway.url;
    if (
      gateway.url &&
      (gateway.url.startsWith("http://") || gateway.url.startsWith("https://"))
    ) {
      // If it's a full URL, we might need to extract the identifier differently
      // For now, let's URL encode it to handle special characters
      gatewayIdentifier = encodeURIComponent(String(gateway.url));
      console.log(
        "🔧 Detected full URL, using encoded identifier:",
        gatewayIdentifier,
      );
    }

    const endpoint = `${baseUrl}/gateways/${gatewayIdentifier}/status`;
    console.log("🌐 Final endpoint:", endpoint);

    try {
      const response = await authenticatedFetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("📡 getGatewayStatus response:", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ getGatewayStatus error response:", errorText);
        throw new Error(
          `Gateway status request failed: ${response.status} ${response.statusText} - ${errorText}`,
        );
      }

      const result = await processResponse(response);
      console.log("✅ getGatewayStatus success:", result);
      return result;
    } catch (error) {
      console.error("💥 getGatewayStatus error:", error);
      throw error;
    }
  };

  const configureDevicesOnGateway = async (gateway: Gateway, devices: []) => {
    console.log("🔍 configureDevicesOnGateway called with gateway:", {
      gateway,
      url: gateway.url,
      devicesCount: devices.length,
    });

    // Check if gateway.url is a full URL and extract the identifier
    let gatewayIdentifier = gateway.url;
    if (
      gateway.url &&
      (gateway.url.startsWith("http://") || gateway.url.startsWith("https://"))
    ) {
      // If it's a full URL, we might need to extract the identifier differently
      // For now, let's URL encode it to handle special characters
      gatewayIdentifier = encodeURIComponent(String(gateway.url));
      console.log(
        "🔧 Detected full URL, using encoded identifier:",
        gatewayIdentifier,
      );
    }

    const endpoint = `${baseUrl}/gateways/${gatewayIdentifier}/devices/configure`;
    console.log("🌐 Final endpoint:", endpoint);

    try {
      const response = await authenticatedFetch(endpoint, {
        method: "PUT",
        body: JSON.stringify(devices),
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("📡 configureDevicesOnGateway response:", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "❌ configureDevicesOnGateway error response:",
          errorText,
        );
        throw new Error(
          `Gateway devices configuration failed: ${response.status} ${response.statusText} - ${errorText}`,
        );
      }

      const result = await processResponse(response);
      console.log("✅ configureDevicesOnGateway success:", result);
      return result;
    } catch (error) {
      console.error("💥 configureDevicesOnGateway error:", error);
      throw error;
    }
  };

  // Administration: Refresh (activate) all digital twins for a gateway. The
  // server resolves the gateway's assets, marshals them and pushes them to the
  // running gateway in batches — a single call instead of fetch-then-configure.
  const refreshDevicesOnGateway = async (gateway: Gateway, batchSize?: number) => {
    let gatewayIdentifier = gateway.url;
    if (
      gateway.url &&
      (gateway.url.startsWith("http://") || gateway.url.startsWith("https://"))
    ) {
      gatewayIdentifier = encodeURIComponent(String(gateway.url));
    }

    const queryString = batchSize ? `?batchSize=${batchSize}` : "";
    const endpoint = `${baseUrl}/gateways/${gatewayIdentifier}/refresh${queryString}`;

    const response = await authenticatedFetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Gateway refresh failed: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return processResponse(response);
  };

  // Administration: Get cache status
  const getCacheStatus = async () => {
    const response = await authenticatedFetch(
      `${baseUrl}/admin/tools/cache/status`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      const errorText = await response.text();
      console.error("Cache status API error:", response.status, errorText);
      throw new Error(`Cache status failed: ${response.status} ${errorText}`);
    }
  };

  // Administration: Send administrative emails (root admin only)
  const sendAdministrativeEmail = async (payload: {
    roles: string[] | null;
    customers: string[] | null;
    template: string;
    test: boolean;
  }) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/email`,
      {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      const errorData = await response.json().catch(() => ({}));
      throw new ApplicationError(
        errorData.error ||
          `Request failed: ${response.status} ${response.statusText}`,
      );
    }
  };

  // Administration: Get overall marketplace statistics (administrators only)
  const getAdministrationMarketplaceStatistics = async () => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/marketplace/statistics`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to get administration marketplace statistics: ${response.statusText}`,
      );
    }

    return response.json();
  };

  const getCustomerToolConfiguration = async (customerId: string) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/configuration`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  const updateCustomerToolConfiguration = async (
    customerId: string,
    configuration: Partial<{ dontUseWebsearch: boolean; marketplacePublishing: boolean; notificationEmail: string }>,
  ) => {
    const response = await authenticatedFetch(
      `${baseUrl}/administration/customers/${customerId}/configuration`,
      {
        method: "PUT",
        body: JSON.stringify({ configuration }),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return processResponse(response);
  };

  // Administration: Refresh agentic tool caches
  const refreshAgenticToolCaches = async () => {
    const response = await authenticatedFetch(
      `${baseUrl}/admin/tools/cache/refresh-published`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      const errorText = await response.text();
      console.error("Cache refresh API error:", response.status, errorText);
      throw new Error(`Cache refresh failed: ${response.status} ${errorText}`);
    }
  };

  const getUserOperationStats = async (
    operation: string | null,
    tenant: string | null,
    from: Date,
    to: Date,
    bucket: "hour" | "day",
    tz: string,
  ) => {
    let url = `${baseUrl}/admin/user-operations/stats?from=${from.toISOString()}&to=${to.toISOString()}&bucket=${bucket}&tz=${encodeURIComponent(tz)}`;
    if (operation) url += `&operation=${encodeURIComponent(operation)}`;
    if (tenant) url += `&tenant=${encodeURIComponent(tenant)}`;
    const response = await authenticatedFetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  return {
    getCustomers,
    getUserOperationStats,
    createCustomer,
    updateCustomer,
    updateCustomerLogo,
    deleteCustomer,
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    resendInvitation,
    ingestData,
    getInvoiceForecast,
    getAllInvoices,
    updateInvoices,
    getInvoices,
    getInvoiceById,
    deleteInvoice,
    runBillingGlobal,
    runBilling,
    updateEntity,
    createOrUpdateProduct,
    uploadProductDocuments,
    uploadEntityDocuments,
    createOrUpdateCustomerTheme,
    getLatestOperationalState: getLatestOperatingState,
    createFloor,
    updateFloor,
    deleteFloor,
    updateFloorplanImage,
    mapFloorDwg,
    detectPlanType,
    analyzeFloorplanImage,
    mapBuildingDwgZip,
    createSpace,
    updateSpace,
    deleteSpace,
    getAssetParts,
    createAsset,
    updateAsset,
    deleteAsset,
    updateAssetPhotos,
    getProjects,
    createProject,
    updateProject,
    deleteProject,
    getProjectEntitlements,
    createProjectEntitlement,
    updateProjectEntitlement,
    deleteProjectEntitlement,
    createLegalPerson,
    updateLegalPerson,
    deleteLegalPerson,
    createGateway,
    updateGateway,
    deleteGateway,
    getDevicesFromGateway,
    getGatewayStatus,
    configureDevicesOnGateway,
    refreshDevicesOnGateway,
    getCacheStatus,
    getAdministrationMarketplaceStatistics,
    refreshAgenticToolCaches,
    sendAdministrativeEmail,
    getCustomerToolConfiguration,
    updateCustomerToolConfiguration,
    getCustomerRoles,
  };
};
